import { Component, computed } from "@angular/core";
import { ColDef, GridOptions } from "ag-grid-community";

import { CaseContextRecordPanelBase } from "@app/case-context/components/case-context-record-panel-base.directive";
import { CaseContextRecordGridComponent } from "@app/case-context/components/case-context-record-grid/case-context-record-grid.component";
import {
  caseCountValueGetter,
  labelValueFormatter,
  recordDateValueFormatter,
  recordCodeValueGetter,
  recordLabelValueGetter,
} from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { InmateOffenseRecord } from "@app/case-context/models/inmate-offense-record.model";
import { PHMS_CASE_CONTEXT_TERMINOLOGY } from "@app/phms/phms-case-context.terminology";
import { PHMS_CASE_TERMINOLOGY } from "@app/phms/phms-case.terminology";
import { AppGridColumnTypes } from "@app/shared/components/tables/ag-grid-cells/app-grid-column-types";
import { createTerminologyGridHeader } from "@app/shared/terminology/terminology-grid-header";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

const DATE_CELL_CLASS = "case-context-record-grid__cell--date";
const STATUS_CELL_CLASS = "case-context-record-grid__cell--status";
const SUPPORTING_CELL_CLASS = "case-context-record-grid__cell--supporting";

/**
 * Offense-records panel: accepts "offense" rows plus uncategorized rows.
 */
@Component({
  selector: "phms-case-context-offense-panel",
  standalone: true,
  imports: [CaseContextRecordGridComponent, TerminologyIdentityDirective],
  templateUrl: "./case-context-offense-panel.component.html",
})
export class CaseContextOffensePanelComponent extends CaseContextRecordPanelBase {
  readonly sectionUsage = PHMS_CASE_CONTEXT_TERMINOLOGY.offenses;
  readonly sectionText = createTerminologyUsageSignals(this.sectionUsage);
  readonly caseText = createTerminologyUsageSignals(PHMS_CASE_TERMINOLOGY.caseContextOffense);
  readonly caseCountHeader = createTerminologyGridHeader(PHMS_CASE_TERMINOLOGY.caseContextOffense.countColumn, this.caseText.countColumn);
  readonly columnDefs = computed<ColDef<InmateOffenseRecord>[]>(() => [
    {
      field: "eventDate",
      headerName: "Date",
      type: AppGridColumnTypes.DateOnlyColumn,
      width: 108,
      minWidth: 104,
      maxWidth: 116,
      sort: "desc",
      cellClass: DATE_CELL_CLASS,
      valueFormatter: recordDateValueFormatter,
    },
    {
      field: "recordCode",
      headerName: "Offense code",
      width: 120,
      minWidth: 110,
      cellClass: SUPPORTING_CELL_CLASS,
      valueGetter: recordCodeValueGetter,
    },
    {
      field: "recordLabel",
      headerName: "Offense",
      flex: 1,
      minWidth: 200,
      valueGetter: recordLabelValueGetter,
    },
    {
      colId: "caseCount",
      ...this.caseCountHeader(),
      width: 100,
      minWidth: 92,
      maxWidth: 118,
      cellClass: SUPPORTING_CELL_CLASS,
      valueGetter: caseCountValueGetter,
    },
    {
      field: "dispositionCode",
      headerName: "Disposition",
      width: 108,
      minWidth: 96,
      maxWidth: 126,
      cellClass: SUPPORTING_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
    {
      field: "recordStatusCode",
      headerName: "Status",
      width: 92,
      minWidth: 84,
      maxWidth: 108,
      cellClass: STATUS_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
  ]);

  readonly agGridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    enableCellTextSelection: true,
  };

  protected accepts(record: InmateOffenseRecord): boolean {
    return record.recordCategoryCode === "offense" || record.recordCategoryCode == null;
  }
}
